<template>
  <div class="flex items-center justify-center">
    <div
      class="flex items-center rounded-lg px-2 py-1 bg-white bg-opacity-40 border border-gray-100 shadow-sm"
      style="backdrop-filter: blur(6px);"
    >
      <button
        type="button"
        @click="prev"
        class="hidden sm:inline-flex items-center p-1.5 mr-1 border-2 border-b-3 border-gray-600 rounded-md shadow-sm text-gray-700 bg-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-50"
        style="box-shadow: inset 0px -4px 0px #9CA3AF, inset 0 -5px 0 0 #F3F4F6, inset 0px 1px 0px 1px #F3F4F6;"
        title="Previous song"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        type="button"
        @click="toggle"
        class="inline-flex items-center p-1.5 border-2 border-b-3 border-gray-600 rounded-md shadow-sm text-gray-700 bg-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-50"
        style="box-shadow: inset 0px -4px 0px #9CA3AF, inset 0 -5px 0 0 #F3F4F6, inset 0px 1px 0px 1px #F3F4F6;"
        :title="playing ? 'Pause LoFi' : 'Play LoFi'"
      >
        <svg v-if="playing" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M6 4a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1zm8 0a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path d="M6.5 5.5A1.5 1.5 0 018.8 4.3l7.2 4.1a1.5 1.5 0 010 2.6l-7.2 4.1A1.5 1.5 0 016.5 13.9v-8.4z" />
        </svg>
      </button>

      <div class="ml-2 mr-2 leading-tight min-w-0 hidden sm:block">
        <div class="flex items-center gap-1">
          <p class="text-xs text-gray-500 uppercase">LoFi Radio</p>
          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            Experimental
          </span>
        </div>
        <button
          type="button"
          @click.stop="showPlaylist = !showPlaylist"
          class="text-left text-sm font-semibold text-gray-800 hover:underline"
          style="max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
          :title="'Playlist (' + playlist.length + ') – click to choose'"
        >
          {{ songName }}
        </button>

        <div class="flex items-center mt-1">
          <span class="text-xs text-gray-600" style="min-width: 78px;">
            {{ timeLabel }}
          </span>
          <input
            class="ml-2"
            type="range"
            min="0"
            :max="Math.max(1, Math.floor(durationSec))"
            step="1"
            :value="Math.floor(seekPreviewSec !== null ? seekPreviewSec : positionSec)"
            @input="onSeekInput"
            @change="onSeekCommit"
            :title="'Seek'"
            style="width: 220px;"
          />
        </div>
      </div>

      <div class="relative flex items-center">
        <button
          type="button"
          @click.stop="showVolume = !showVolume"
          class="inline-flex items-center p-1.5 ml-1 border-2 border-b-3 border-gray-600 rounded-md shadow-sm text-gray-700 bg-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-50"
          style="box-shadow: inset 0px -4px 0px #9CA3AF, inset 0 -5px 0 0 #F3F4F6, inset 0px 1px 0px 1px #F3F4F6;"
          title="Volume"
        >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 010 12.728" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.463 8.288a5.25 5.25 0 010 7.424" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 8.25l4.5-3v13.5l-4.5-3H3a1.5 1.5 0 01-1.5-1.5v-4.5A1.5 1.5 0 013 8.25h3.75z" />
            </svg>
        </button>

        <div
          v-if="showVolume"
          class="absolute bottom-12 right-0 rounded-lg px-3 py-2 bg-white border border-gray-200 shadow-lg"
          style="min-width: 200px;"
        >
          <p class="text-xs text-gray-500 uppercase">Volume</p>
          <input
            class="w-full"
            type="range"
            min="0"
            max="1"
            step="0.01"
            v-model.number="volume"
            @input="onVolume"
            :title="'Volume: ' + Math.round(volume * 100) + '%'"
          />
        </div>
      </div>

      <button
        type="button"
        @click="next"
        class="hidden sm:inline-flex items-center p-1.5 ml-1 border-2 border-b-3 border-gray-600 rounded-md shadow-sm text-gray-700 bg-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-50"
        style="box-shadow: inset 0px -4px 0px #9CA3AF, inset 0 -5px 0 0 #F3F4F6, inset 0px 1px 0px 1px #F3F4F6;"
        title="Next song"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <button
        type="button"
        @click="showCloseConfirmation = true"
        class="inline-flex items-center p-1.5 ml-1 border-2 border-b-3 border-gray-600 rounded-md shadow-sm text-gray-700 bg-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-50"
        style="box-shadow: inset 0px -4px 0px #9CA3AF, inset 0 -5px 0 0 #F3F4F6, inset 0px 1px 0px 1px #F3F4F6;"
        title="Hide LoFi Radio"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>

      <div class="ml-2 sm:hidden text-xs font-semibold text-gray-700">
        {{ playing ? 'On' : 'Off' }}
      </div>
    </div>

    <div v-if="showPlaylist" class="absolute bottom-14 left-1/2 transform -translate-x-1/2 z-50">
      <div class="rounded-lg bg-white border border-gray-200 shadow-xl p-2" style="width: 320px; max-width: calc(100vw - 24px);">
        <div class="flex items-center justify-between px-1 pb-1">
          <p class="text-xs text-gray-500 uppercase">Playlist</p>
          <button type="button" class="text-xs text-gray-600 hover:underline" @click="showPlaylist = false">Close</button>
        </div>
        <div class="max-h-64 overflow-auto">
          <button
            v-for="s in playlist"
            :key="s.id"
            type="button"
            @click="selectSong(s.id)"
            class="w-full text-left px-2 py-2 rounded hover:bg-gray-100"
            :class="s.id === songId ? 'bg-gray-100' : ''"
          >
            <div class="text-sm font-semibold text-gray-800" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ s.name }}</div>
            <div class="text-xs text-gray-500">{{ s.key }} • {{ s.bpm }} BPM</div>
          </button>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showCloseConfirmation" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="showCloseConfirmation = false">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900">Hide LoFi Radio</h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  Are you sure you want to hide the LoFi Radio? You can enable it again in your <strong>Profile settings</strong>.
                </p>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              @click="confirmClose"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Hide Radio
            </button>
            <button
              type="button"
              @click="showCloseConfirmation = false"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { LOFI_SONGS, createLofiRadioEngine } from '@/utils/lofiRadio';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatTime(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  return `${pad2(m)}:${pad2(s % 60)}`;
}

export default {
  name: 'LoFiRadio',
  data() {
    return {
      engine: null,
      onDocumentClick: null,
      playing: false,
      playlist: LOFI_SONGS,
      songName: LOFI_SONGS[0].name,
      songId: LOFI_SONGS[0].id,
      volume: 0.25,
      durationSec: 0,
      positionSec: 0,
      seekPreviewSec: null,
      showPlaylist: false,
      showVolume: false,
      showCloseConfirmation: false
    };
  },
  emits: ['close'],
  computed: {
    timeLabel() {
      return `${formatTime(this.seekPreviewSec !== null ? this.seekPreviewSec : this.positionSec)} / ${formatTime(this.durationSec)}`;
    }
  },
  mounted() {
    // Create engine lazily; audio context will only start after user click.
    this.engine = createLofiRadioEngine({
      song: LOFI_SONGS[0],
      onStateChange: (state) => {
        this.playing = state.playing;
        this.songName = state.songName;
        this.songId = (this.playlist[state.songIndex] && this.playlist[state.songIndex].id) || this.songId;
        this.volume = state.volume;
        this.durationSec = state.durationSec;
        if (this.seekPreviewSec === null) this.positionSec = state.positionSec;
      }
    });

    const state = this.engine.getState();
    this.playing = state.playing;
    this.songName = state.songName;
    this.volume = state.volume;
      this.durationSec = state.durationSec;
      this.positionSec = state.positionSec;

    this.onDocumentClick = (e) => {
      // Close popovers when user clicks outside of this component.
      // Guard for edge cases during unmount.
      if (!this.$el) return;
      if (this.$el.contains(e.target)) return;
      this.showPlaylist = false;
      this.showVolume = false;
    };

    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick);
      this.onDocumentClick = null;
    }
    if (this.engine) {
      this.engine.destroy();
      this.engine = null;
    }
  },
  methods: {
    async toggle() {
      if (!this.engine) return;
      try {
        if (this.playing) {
          await this.engine.stop();
        } else {
          await this.engine.start();
          // Track play event with EngageKit
          if (typeof window.EngageKit !== 'undefined') {
            window.EngageKit.track('LoFi Radio Played');
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('LoFiRadio failed to start:', e);
      }
    },
    prev() {
      if (!this.engine) return;
      this.showPlaylist = false;
      this.seekPreviewSec = null;
      this.engine.prevSong();
    },
    next() {
      if (!this.engine) return;
      this.showPlaylist = false;
      this.seekPreviewSec = null;
      this.engine.nextSong();
    },
    selectSong(id) {
      if (!this.engine) return;
      this.showPlaylist = false;
      this.seekPreviewSec = null;
      this.engine.selectSongById(id);
    },
    onVolume() {
      if (!this.engine) return;
      this.engine.setVolume(this.volume);
    },
    onSeekInput(e) {
      const v = Number(e && e.target ? e.target.value : 0);
      this.seekPreviewSec = v;
    },
    onSeekCommit(e) {
      if (!this.engine) return;
      const v = Number(e && e.target ? e.target.value : 0);
      this.seekPreviewSec = null;
      this.engine.seek(v);
    },
    confirmClose() {
      this.showCloseConfirmation = false;
      this.$emit('close');
    }
  }
};
</script>
